/**
 * Ancrage d'un commentaire sur le contenu du site.
 *
 * Une ancre est `{ scope, blockId, x, y }` où `x` / `y` sont des **ratios de la
 * boîte du bloc** (0..1), jamais des pixels : la pastille suit le bloc quand il
 * se déplace, se redimensionne ou passe en largeur mobile.
 *
 * Le bloc visé se retrouve sans qu'aucun bloc n'ait été modifié : tous posent
 * déjà leur `id` Puck sur leur élément racine dans le viewer. On remonte donc
 * les ancêtres du point cliqué jusqu'au premier `id` connu de la page. Surtout
 * pas de wrapper par bloc — il casserait les mises en page flex/grid.
 */

const SKELETON_SCOPES = ['header', 'footer'];

const blockId = (block) => String(block?.props?.id ?? block?.id ?? '');

/**
 * Ids des blocs **posés directement sur la page**, sans descendre dans les
 * zones. C'est l'unité de l'attention par bloc : un Bloc et le Texte qu'il
 * contient occupent la même surface à l'écran, donc mesurer les deux compte le
 * même regard deux fois — deux rectangles superposés sur la carte, et chaque
 * contenu en double dans le classement.
 *
 * Le survol en hérite sans rien de plus : `closestBlockElement()` remonte au
 * **premier** id connu, donc survoler un Texte désigne le Bloc qui le porte.
 */
export function pageRootBlockIds(puckData) {
  const ids = new Set();
  (puckData?.content || []).forEach((block) => {
    const id = blockId(block);
    if (id) ids.add(id);
  });
  return ids;
}

/** Ids de tous les blocs d'une page : racine + zones imbriquées (onglets…). */
export function pageBlockIds(puckData) {
  const ids = new Set();
  (puckData?.content || []).forEach((block) => {
    const id = blockId(block);
    if (id) ids.add(id);
  });
  const zones = (puckData?.zones && typeof puckData.zones === 'object') ? puckData.zones : {};
  Object.values(zones).forEach((blocks) => {
    (blocks || []).forEach((block) => {
      const id = blockId(block);
      if (id) ids.add(id);
    });
  });
  return ids;
}

/** Ids de blocs de tout le site, par scope — ce qui permet de reconnaître les
 *  fils devenus orphelins (bloc ou page supprimés depuis le commentaire). */
export function siteBlockIds(webData) {
  const byScope = new Map();
  Object.entries(webData?.pages || {}).forEach(([slug, page]) => {
    byScope.set(`page:${slug}`, pageBlockIds(page?.puckData));
  });
  SKELETON_SCOPES.forEach((scope) => {
    const section = webData?.skeleton?.[scope];
    if (section) byScope.set(scope, pageBlockIds(section));
  });
  return byScope;
}

const clampRatio = (value) => Math.min(1, Math.max(0, value));

/** Premier ancêtre (l'élément lui-même compris) dont l'`id` est un bloc connu.
 *  La remontée s'arrête à `root` : un clic sur la chrome du site n'ancre rien. */
export function closestBlockElement(element, blockIds, root) {
  let current = element;
  while (current && current !== root?.parentNode) {
    const id = current.id;
    if (id && blockIds.has(id)) return current;
    if (current === root) return null;
    current = current.parentElement;
  }
  return null;
}

/**
 * Ancre d'un clic. Sans bloc reconnu sous le curseur (fond de page, gouttière),
 * l'ancre est celle de la page : `blockId` nul, ratios pris sur la racine.
 */
export function anchorFromPoint({ element, clientX, clientY, blockIds, root, scope }) {
  if (!root || !scope) return null;

  const target = closestBlockElement(element, blockIds || new Set(), root) || root;
  const rect = target.getBoundingClientRect();
  if (!rect.width || !rect.height) return null;

  return {
    scope,
    blockId: target === root ? null : target.id,
    x: clampRatio((clientX - rect.left) / rect.width),
    y: clampRatio((clientY - rect.top) / rect.height),
  };
}

/** Élément qui porte l'ancre, ou la racine du site pour une ancre de page. */
export function anchorElement(root, anchor) {
  if (!root || !anchor) return null;
  if (!anchor.blockId) return root;
  const escape = window.CSS?.escape || ((value) => value);
  return root.querySelector(`#${escape(anchor.blockId)}`);
}

/** Position de la pastille dans le repère de la racine du site (px).
 *  Séparée de la lecture du DOM pour rester testable et pour n'appeler
 *  `getBoundingClientRect` qu'une fois par bloc au recalcul. */
export function pinPosition(anchor, targetRect, rootRect) {
  if (!anchor || !targetRect || !rootRect) return null;
  return {
    left: (targetRect.left - rootRect.left) + (anchor.x * targetRect.width),
    top: (targetRect.top - rootRect.top) + (anchor.y * targetRect.height),
  };
}
